import { Button, type ButtonProps } from '@/components/common/button'
import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import type { OpportunityLoader } from '@/routes/opportunity.$network.$vaultId'
import { cn } from '@/utils/cn'
import { ETH_TOKENS } from '@vaultsfyi/common/constants'
import { fromDecimal } from '@vaultsfyi/common/utils'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { type Dispatch, type SetStateAction, useMemo } from 'react'
import type { useForm } from 'react-hook-form'
import { useLoaderData } from 'react-router'
import { formatUnits } from 'viem'

const ONE_IN_DECIMAL = fromDecimal(1)
const STABLECOIN_BUTTON_AMOUNTS = ['1000', '5000', '20000']
const ETH_BUTTON_AMOUNTS = ['0.5', '5', '20']
const AMOUNT_DECIMALS = [0.25, 0.5, 1]

interface Props {
  form: ReturnType<typeof useForm>
  maxInputAmount: string
  setUseAllFunds?: Dispatch<SetStateAction<boolean>>
  className?: string
}

export const AmountButtons = ({ form, maxInputAmount, setUseAllFunds, className }: Props) => {
  const { vault } = useLoaderData<OpportunityLoader>()
  const { isConnected } = useAccountContext()
  const { trackEvent } = useMixpanel()

  const buttonAmounts = useMemo(() => {
    if (ETH_TOKENS.includes(vault.asset.symbol)) {
      return ETH_BUTTON_AMOUNTS
    } else {
      return STABLECOIN_BUTTON_AMOUNTS
    }
  }, [STABLECOIN_BUTTON_AMOUNTS, ETH_BUTTON_AMOUNTS, vault.asset.symbol])

  return (
    <div className={cn('grid max-w-full grid-cols-3 gap-3', className)}>
      {isConnected
        ? AMOUNT_DECIMALS.map((decimal) => {
            const formAmount = form.watch('amount')
            const buttonAmount = formatUnits(
              (BigInt(maxInputAmount) * BigInt(fromDecimal(decimal))) / BigInt(ONE_IN_DECIMAL),
              vault.asset.decimals
            )
            return (
              <AmountButton
                key={fromDecimal(decimal)}
                isActive={formAmount !== '0' && formAmount === buttonAmount}
                onClick={() => {
                  form.setValue(
                    'amount',
                    formatUnits(
                      (BigInt(maxInputAmount) * BigInt(fromDecimal(decimal))) / BigInt(ONE_IN_DECIMAL),
                      vault.asset.decimals
                    ),
                    {
                      shouldValidate: true,
                    }
                  )
                  setUseAllFunds?.(fromDecimal(decimal) === ONE_IN_DECIMAL)
                  trackEvent('Prepopulated amount button clicked', { Amount: formatDecimalToPercentage(decimal) })
                }}
              >
                <span className="max-w-full truncate">
                  {fromDecimal(decimal) === ONE_IN_DECIMAL
                    ? 'Max'
                    : formatDecimalToPercentage(decimal, { fractionDigits: 0 })}
                </span>
              </AmountButton>
            )
          })
        : buttonAmounts.map((amount) => {
            return (
              <AmountButton
                key={amount}
                isActive={form.watch('amount') === amount}
                onClick={() => {
                  form.setValue('amount', amount, { shouldValidate: true })
                  trackEvent('Prepopulated amount button clicked', { Amount: `${amount} ${vault.asset.symbol}` })
                }}
              >
                <span className="max-w-full overflow-hidden">
                  {amount} <span className="max-w-full truncate">{vault.asset.symbol}</span>
                </span>
              </AmountButton>
            )
          })}
    </div>
  )
}

interface AmountButtonProps extends ButtonProps {
  isActive: boolean
}

const AmountButton = ({ isActive, ...props }: AmountButtonProps) => {
  return (
    <Button
      variant={isActive ? 'primary' : 'input'}
      className={cn(
        'max-w-full overflow-hidden rounded-xl px-2',
        'ring-inset focus-visible:border-focus',
        !isActive && 'bg-page'
      )}
      {...props}
    />
  )
}
